import { LinkProps, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useReducer } from 'react';
import { SortingState } from '@tanstack/react-table';
import { uniqBy } from 'es-toolkit';
import { transformMap, transformSearchToStateObject } from './utils';
import { SearchSchemaInput } from '../pagination';

type State = SearchSchemaInput;
export type FilterAction =
  | { type: 'paginate'; payload: number }
  | { type: 'setLimit'; payload: 10 | 20 | 50 | undefined }
  | { type: 'setQuery'; payload: string }
  | { type: 'setSort'; payload: SortingState }
  | { type: 'setFilter'; payload: [id: string, value: string] };

function reducer(state: State, action: FilterAction): SearchSchemaInput {
  const { type, payload } = action;
  switch (type) {
    case 'paginate':
      return { ...state, page: payload };
    case 'setLimit':
      return { ...state, page: 1, limit: payload };
    case 'setQuery':
      return { ...state, page: 1, search: payload };
    case 'setSort': {
      if (!payload.length) {
        return { ...state, sort: undefined };
      }
      return {
        ...state,
        page: 1,
        sort: [payload[0].id, payload[0].desc ? 'asc' : 'desc'],
      };
    }
    case 'setFilter': {
      return {
        ...state,
        page: 1,
        filter: uniqBy([payload, ...(state.filter ?? [])], (item) => item[0]),
      };
    }
    default:
      return state;
  }
}

export function useFilter(from: LinkProps['from']) {
  const search = useSearch({
    strict: false,
  });

  const initialState = transformSearchToStateObject(search);

  const [state, dispatch] = useReducer(reducer, initialState);

  const navigate = useNavigate();

  useEffect(() => {
    navigate({
      from,
      search: {
        page: state.page,
        limit: state.limit,
        search: state.search,
        sort: normalizeSort(state.sort),
        filter: normalizeFilter(state.filter),
      },
    });
  }, [state, navigate, from]);

  return { state, dispatch, mappedState: transformMap(state) };
}

function normalizeSort(sort: State['sort']) {
  return sort ? `${sort[0]},${sort[1]}` : undefined;
}

function normalizeFilter(filter: State['filter']) {
  return filter?.map((filter) => `${filter[0]},${filter[1]}`);
}
